import { FC, useEffect, useState, useMemo } from "react";
import { Box, useTheme } from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  ReactTable,
  useDispatchOnParams,
  Topbar
} from "@vilocnv/allsetra-core";

// Data
import { useAppSelector, useCurrentUser } from "hooks";
import { selectDrawerSelectedAccountState } from "app/data/selectors";
import { useTranslation } from "react-i18next";
import { getDriverMileageByQueryThunk } from "../../store/actions/get-driver-mileage.action";
import { selectDriverMileageState } from "../../store/selectors/driver-mileage.selector";
import { DRIVER_MILEAGE_TABLE_COLUMNS } from "../../constants/driver-mileage-table-columns";

const DriverMileage: FC = () => {
  const theme = useTheme();
  const { isDriver } = useCurrentUser();
  const navigate = useNavigate();

  // Global State
  const { drawerSelectedAccountId: accountId } = useAppSelector(
    selectDrawerSelectedAccountState
  );
  const { driverMileageList, totalRecords, loading } = useAppSelector(
    selectDriverMileageState
  );

  const { t, i18n } = useTranslation([
    "translation",
    "tableHeadingsTranslation"
  ]);

  const tableColumns = useMemo(
    () => DRIVER_MILEAGE_TABLE_COLUMNS(t, driverMileageList),
    [t, i18n.language, driverMileageList]
  );

  // Local State
  const [searchByValue, setSearchByValue] =
    useState<string>("driver.firstName");

  useDispatchOnParams(getDriverMileageByQueryThunk, {
    args: accountId ? { accountId: accountId } : undefined,
    searchByField: searchByValue
  });

  useEffect(() => {
    if (isDriver) {
      navigate("/dashboard", { replace: true });
    }
  }, [isDriver, navigate]);

  return (
    <main>
      <Topbar
        theme={theme}
        title={t("drawerMenuLinks.driverMileage")}
        searchField={{
          placeholder: t("common.search"),
          sx: {
            "& .MuiSvgIcon-root": {
              fill: "#959EB2"
            }
          }
        }}
        searchByOptions={[
          {
            value: "driver.firstName",
            label: t("tableHeading.driverName", {
              ns: "tableHeadingsTranslation"
            })
          },
          {
            value: "driver.email",
            label: t("tableHeading.driverEmail", {
              ns: "tableHeadingsTranslation"
            })
          }
        ]}
        searchByValue={searchByValue}
        setSearchByValue={setSearchByValue}
        translator={t}
        searchByWidth="160px"
      />
      <Box mx={4}>
        <ReactTable
          columns={tableColumns}
          data={driverMileageList ?? []}
          isSelectable={false}
          isLoading={loading}
          paginationTotalRows={totalRecords}
          sx={undefined}
          moduleName="driverMileage"
          tableMainBoxStyles={{
            height: "calc(100vh - 314px)",
            border: "1px solid #D5D9E8",
            borderRadius: "10px",
            marginBottom: "10vh",
            padding: "1px",
            "& tbody": {
              "& tr": {
                "& td": {
                  padding: "10px 16px"
                }
              }
            }
          }}
          mainTableStyles={{
            height: driverMileageList?.length > 0 ? "fit-content" : "100%"
          }}
          translator={t}
        />
      </Box>
    </main>
  );
};

export default DriverMileage;
